import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

const D3Chart = ({ config }) => {
    const jsonConfig = JSON.parse(config.text);
    const svgRef = useRef();
    const containerRef = useRef();
    const tooltipRef = useRef();
    const [dimensions, setDimensions] = useState({ width: 600, height: 400 });

    useEffect(() => {
        if (!jsonConfig || !jsonConfig.chartType) return;

        const updateDimensions = () => {
            if (containerRef.current) {
                setDimensions({
                    width: containerRef.current.clientWidth,
                    height: 400 // 🔹 Max height is fixed at 400px
                });
            }
        };

        updateDimensions();
        window.addEventListener("resize", updateDimensions);
        return () => window.removeEventListener("resize", updateDimensions);
    }, []);

    useEffect(() => {
        if (!jsonConfig || !jsonConfig.chartType) return;
        const { chartType, xKey, yKey, data, tooltip } = jsonConfig;
        const stackKeys = jsonConfig.stackKeys || []; // Ensure stackKeys is always an array
    
        const yKeys = jsonConfig.yKeys || [yKey]; // 🔹 Support multiple yKeys for multi-line charts
    
        const { width, height } = dimensions;
        const margin = { top: 20, right: 30, bottom: 50, left: 50 };
    
        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();
        svg.attr("width", width).attr("height", height);
    
        // 🔹 Use scaleBand() for bar charts instead of scalePoint()
        const xScale = d3.scaleBand()
            .domain(data.map((d) => d[xKey]))
            .range([margin.left, width - margin.right])
            .padding(0.2);
    
        // 🔹 Compute yScale dynamically for multiple yKeys (line charts) and stackKeys (stacked bars)
        const yScale = d3.scaleLinear()
            .domain([
                0,
                d3.max(data, (d) => stackKeys.length
                    ? d3.sum(stackKeys.map((key) => d[key])) // Sum stack values
                    : Math.max(...yKeys.map((key) => d[key]))) // 🔹 Find max across multiple yKeys
            ])
            .nice()
            .range([height - margin.bottom, margin.top]);
    
        // 🔹 Create Axes
        const xAxis = d3.axisBottom(xScale);
        const yAxis = d3.axisLeft(yScale);
    
        svg.append("g")
            .attr("transform", `translate(0, ${height - margin.bottom})`)
            .call(xAxis)
            .selectAll("text")
            .attr("transform", "rotate(-45)")
            .style("text-anchor", "end");
    
        svg.append("g").attr("transform", `translate(${margin.left},0)`).call(yAxis);
    
        // 🔹 Initialize Tooltip if Enabled
        const tooltipDiv = d3.select(tooltipRef.current);
        if (tooltip?.enabled) {
            tooltipDiv
                .style("position", "absolute")
                .style("visibility", "hidden")
                .style("background", tooltip.style?.background || "rgba(0,0,0,0.8)")
                .style("color", tooltip.style?.color || "#fff")
                .style("border-radius", tooltip.style?.borderRadius || "5px")
                .style("padding", tooltip.style?.padding || "8px")
                .style("font-size", "12px")
                .style("pointer-events", "none");
        }
    
        // 🔹 Switch Case for Chart Type
        switch (chartType) {
            case "line":
                drawLineChart(svg, data, xScale, yScale, xKey, yKeys, tooltipDiv, tooltip?.enabled, containerRef);
                break;
            case "bar":
                drawBarChart(svg, data, xScale, yScale, xKey, yKey, width, height, margin, tooltipDiv, tooltip?.enabled, containerRef);
                break;
            case "groupedBar":
                drawGroupedBarChart(svg, data, xScale, yScale, xKey, stackKeys, width, height, margin, tooltipDiv, tooltip?.enabled, containerRef);
                break;
            case "stackedBar":
                drawStackedBarChart(svg, data, xScale, yScale, xKey, stackKeys, width, height, margin, tooltipDiv, tooltip?.enabled, containerRef);
                break;
            case "pie":
                drawPieChart(svg, data, width, height, tooltipDiv, tooltip?.enabled, containerRef);
                break;
            default:
                console.warn(`Unsupported chart type: ${chartType}`);
        }
    }, [jsonConfig, dimensions]);

    return (
        <div ref={containerRef} style={{ width: "100%", height: "100%", maxHeight: "400px", position: "relative" }}>
            <svg ref={svgRef}></svg>
            <div ref={tooltipRef} style={{ position: "absolute", visibility: "hidden" }}></div>
        </div>
    );
};



// ** Line Chart with Corrected Tooltip Positioning **
const drawLineChart = (svg, data, xScale, yScale, xKey, yKeys, tooltip, hoverEnabled, containerRef) => {
    const color = d3.scaleOrdinal(d3.schemeCategory10); // 🔹 Assign different colors for each line

    yKeys.forEach((yKey, index) => {
        const line = d3.line()
            .x((d) => xScale(d[xKey]))
            .y((d) => yScale(d[yKey]))
            .curve(d3.curveMonotoneX);

        svg.append("path")
            .datum(data)
            .attr("fill", "none")
            .attr("stroke", color(index)) // 🔹 Different color per line
            .attr("stroke-width", 2)
            .attr("d", line);

        // 🔹 Add dots if hover is enabled
        if (hoverEnabled) {
            svg.selectAll(`.dot-${yKey}`)
                .data(data)
                .enter()
                .append("circle")
                .attr("cx", (d) => xScale(d[xKey]))
                .attr("cy", (d) => yScale(d[yKey]))
                .attr("r", 5)
                .attr("fill", color(index))
                .on("mouseover", (event, d) => {
                    tooltip.style("visibility", "visible").html(`${yKey}: ${d[yKey]}`);
                })
                .on("mousemove", (event) => {
                    const containerRect = containerRef.current.getBoundingClientRect();
                    tooltip
                        .style("top", `${event.pageY - containerRect.top}px`)
                        .style("left", `${event.pageX - containerRect.left + 10}px`);
                })
                .on("mouseout", () => tooltip.style("visibility", "hidden"));
        }
    });
};

// ** Bar Chart with Corrected Tooltip Positioning **
const drawBarChart = (svg, data, xScale, yScale, xKey, yKey, width, height, margin, tooltip, hoverEnabled, containerRef) => {
    const barWidth = Math.max(10, (width - margin.left - margin.right) / data.length - 10);

    const bars = svg.selectAll(".bar")
        .data(data)
        .enter()
        .append("rect")
        .attr("x", (d) => xScale(d[xKey]) - barWidth / 2)
        .attr("y", (d) => yScale(d[yKey]))
        .attr("width", barWidth)
        .attr("height", (d) => height - margin.bottom - yScale(d[yKey]))
        .attr("fill", "teal");

    if (hoverEnabled) {
        bars.on("mouseover", (event, d) => {
            tooltip.style("visibility", "visible").html(`${d[xKey]}: ${d[yKey]}`);
        })
            .on("mousemove", (event) => {
                const containerRect = containerRef.current.getBoundingClientRect();
                tooltip
                    .style("top", `${event.pageY - containerRect.top}px`)
                    .style("left", `${event.pageX - containerRect.left + 10}px`);
            })
            .on("mouseout", () => tooltip.style("visibility", "hidden"));
    }
};

// ** Pie Chart with Corrected Tooltip Positioning **
const drawPieChart = (svg, data, width, height, tooltip, hoverEnabled, containerRef) => {
    const radius = Math.min(width, height) / 2 - 10;
    const pie = d3.pie().value((d) => d.value);
    const arc = d3.arc().innerRadius(0).outerRadius(radius);
    const color = d3.scaleOrdinal(d3.schemeCategory10);
    const pieData = pie(data);

    const g = svg.append("g").attr("transform", `translate(${width / 2}, ${height / 2})`);

    const slices = g.selectAll("path")
        .data(pieData)
        .enter()
        .append("path")
        .attr("d", arc)
        .attr("fill", (d, i) => color(i));

    if (hoverEnabled) {
        slices.on("mouseover", (event, d) => {
            tooltip.style("visibility", "visible").html(`${d.data.label}: ${d.data.value}`);
        })
            .on("mousemove", (event) => {
                const containerRect = containerRef.current.getBoundingClientRect();
                tooltip
                    .style("top", `${event.pageY - containerRect.top}px`)
                    .style("left", `${event.pageX - containerRect.left + 10}px`);
            })
            .on("mouseout", () => tooltip.style("visibility", "hidden"));
    }
};

// ** Grouped Bar Chart **
const drawGroupedBarChart = (svg, data, xScale, yScale, xKey, stackKeys, width, height, margin, tooltip, hoverEnabled, containerRef) => {
    const groupWidth = (width - margin.left - margin.right) / data.length;
    const barWidth = groupWidth / stackKeys.length - 5;
    const color = d3.scaleOrdinal(d3.schemeCategory10);

    const groups = svg.selectAll(".group")
        .data(data)
        .enter()
        .append("g")
        .attr("transform", d => `translate(${xScale(d[xKey]) - groupWidth / 2}, 0)`);

    stackKeys.forEach((key, i) => {
        groups.append("rect")
            .attr("x", i * barWidth)
            .attr("y", d => yScale(d[key]))
            .attr("width", barWidth)
            .attr("height", d => height - margin.bottom - yScale(d[key]))
            .attr("fill", color(i))
            .on("mouseover", (event, d) => {
                if (hoverEnabled) {
                    tooltip.style("visibility", "visible").html(`${key}: ${d[key]}`);
                }
            })
            .on("mousemove", (event) => {
                const containerRect = containerRef.current.getBoundingClientRect();
                tooltip
                    .style("top", `${event.pageY - containerRect.top}px`)
                    .style("left", `${event.pageX - containerRect.left + 10}px`);
            })
            .on("mouseout", () => tooltip.style("visibility", "hidden"));
    });
};

// ** Stacked Bar Chart Function **
const drawStackedBarChart = (svg, data, xScale, yScale, xKey, stackKeys, width, height, margin, tooltip, hoverEnabled, containerRef) => {
    const color = d3.scaleOrdinal(d3.schemeCategory10);

    // Create stack generator
    const stack = d3.stack()
        .keys(stackKeys)
        .order(d3.stackOrderNone)
        .offset(d3.stackOffsetNone);

    const stackedData = stack(data);

    // Draw stacks
    const groups = svg.selectAll(".stack-group")
        .data(stackedData)
        .enter()
        .append("g")
        .attr("fill", (d, i) => color(i));

    groups.selectAll("rect")
        .data(d => d)
        .enter()
        .append("rect")
        .attr("x", d => xScale(d.data[xKey]))
        .attr("y", d => yScale(d[1])) // Use the top of the stacked bar
        .attr("height", d => yScale(d[0]) - yScale(d[1])) // Stack height
        .attr("width", xScale.bandwidth())
        .on("mouseover", (event, d) => {
            if (hoverEnabled) {
                tooltip.style("visibility", "visible").html(`${d.key}: ${d.data[d.key]}`);
            }
        })
        .on("mousemove", (event) => {
            const containerRect = containerRef.current.getBoundingClientRect();
            tooltip
                .style("top", `${event.pageY - containerRect.top - 10}px`)
                .style("left", `${event.pageX - containerRect.left + 10}px`);
        })
        .on("mouseout", () => tooltip.style("visibility", "hidden"));
};

export default D3Chart;

